/**
 * Every route this host serves — and there are only four.
 *
 * ── Why so few ─────────────────────────────────────────────────────────────
 * This is a TRANSACTIONAL host, not the product's website. Nobody browses here:
 * every visitor arrived from a link we sent them — an SMS, or the app opening a
 * payment WebView — with no account and no session. The source this was ported
 * from (`wt-taheel-landing`) was a full customer web app with sixteen feature
 * areas: login, browsing, a booking wizard, a wallet, an account. None of that
 * belongs on a host whose whole job is to finish one action and get out of the
 * way, and shipping it would mean maintaining a second customer app nobody
 * asked for.
 *
 * The same subset decision was made for Watan Care first; this follows it.
 *
 * ── The token is the credential ────────────────────────────────────────────
 * `confirm-location/:token` carries its token in the PATH, not a query string,
 * and the client does not validate or sign it — it IS the capability, and the
 * backend is what checks it. There is deliberately no auth context anywhere in
 * this app; see `main.tsx`.
 *
 * ── What is deliberately absent ────────────────────────────────────────────
 * `/legal/terms` and `/legal/privacy` existed in the source as hardcoded
 * documents naming ANOTHER company as the operating party, with its commercial
 * registration and VAT numbers. They were deleted rather than rebranded:
 * find-replacing a company name through a legal document and publishing it
 * under our domain is a false legal claim, not a port. `/terms` and `/privacy`
 * read their content from the backend instead, so they say whatever Tadbeer's
 * own documents say (`/privacy` since 2026-10-31).
 */
export const Routes = {
  /** HyperPay COPYandPAY widget for one checkout. */
  payCheckout: {
    path: '/pay/:checkoutId',
    build: (checkoutId: string) => `/pay/${encodeURIComponent(checkoutId)}`,
  },

  /**
   * Where the gateway sends the customer back.
   *
   * Provider-agnostic: Tamara returns here too and is told apart by
   * `?provider=tamara`, which changes only the copy. The reconcile path — poll
   * the backend for the real status — is the same for both, because the
   * customer's browser is never the source of truth about whether money moved.
   */
  payReturn: { path: '/pay/return' },

  /** The link SMSed to a customer to confirm where their visit happens. */
  confirmLocation: {
    path: '/confirm-location/:token',
    build: (token: string) => `/confirm-location/${encodeURIComponent(token)}`,
  },

  /** Terms, fetched from the backend — no document is hardcoded here. */
  terms: { path: '/terms' },

  /** The privacy policy — the `privacy_policy` terms document, also fetched. */
  privacy: { path: '/privacy' },
} as const;
